import { inject, Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { ConfigService } from './config.service';
import { LoginService } from './login.service';
import { JoseJwksValidationHandler } from './jose-validation-handler';
import { APP_ID } from '../app-id';

// enum PartnerTypes {
//   'gmail.com' = 'gmail',
//   'comcast.com' = 'comcast',
//   'sky.com' = 'sky',
//   'cox.com' = 'cox',
//   'cox.net' = 'cox',
//   'sky.net' = 'sky',
//   'softwaresecurityconsultants.com' = 'softwaresecurityconsultants'
// }

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private configService = inject(ConfigService);
  private loginService = inject(LoginService);
  private oauthService = inject(OAuthService);
  private authConfig!: AuthConfig;
  private providersUrl = 'http://localhost/api/1/auth/providers';

  configureOAuthService() {
    if (this.configService.auth) {
      this.authConfig = {
        issuer: APP_ID.issuer,
        redirectUri: this.configService.auth.authRedirectUri,
        clientId: this.configService.auth.clientId,
        requestAccessToken: true,
        requireHttps: false,
        disableAtHashCheck: true,
        oidc: true,
        scope: 'openid profile email',
        showDebugInformation: false,
        disablePKCE: true,
        skipSubjectCheck: true,
        strictDiscoveryDocumentValidation: false,
      };

      this.oauthService.configure(this.authConfig);
      this.oauthService.setStorage(localStorage);
      this.oauthService.tokenValidationHandler =
        new JoseJwksValidationHandler();
      //      this.oauthService.setupAutomaticSilentRefresh({}, 'access_token');
      this.oauthService.silentRefreshRedirectUri =
        window.location.origin + '/assets/silent-refresh.html';
    }
  }

  getToken(): string {
    return this.oauthService.getAccessToken();
  }

  get userName(): string | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const claims = this.oauthService.getIdentityClaims() as any;
    if (claims && claims.name) {
      return claims.name;
    }

    return null;
  }

  get userGivenName(): string | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const claims = this.oauthService.getIdentityClaims() as any;
    if (claims && claims.givenname) {
      return claims.givenname;
    }
    return null;
  }

  get userSurname(): string | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const claims = this.oauthService.getIdentityClaims() as any;
    if (claims && claims.surname) {
      return claims.surname;
    }

    return null;
  }

  get userRoles(): string[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const claims = this.oauthService.getIdentityClaims() as any;
    if (claims && claims.roles) {
      return claims.roles;
    }
    return [];
  }

  get userEmail(): string | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const claims = this.oauthService.getIdentityClaims() as any;
    if (claims && claims.email) {
      return claims.email.toLowerCase();
    }
    return null;
  }

  get partnerName(): string | null {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const claims = this.oauthService.getIdentityClaims() as any;
      if (claims && claims.roles) {
        const lastDelimiterIndex: number = claims.roles[0].lastIndexOf('-');
        return claims.roles[0].substring(0, lastDelimiterIndex).toLowerCase();
      }
    } catch (err) {
      console.error('AuthService get partner name', err);
      return null;
    }
    return null;
  }

  get userId(): string | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const claims = this.oauthService.getIdentityClaims() as any;
    if (claims && claims.email) {
      const userID = claims.email.toLowerCase();
      return userID;
    }
    return null;
  }

  get expiryTime(): number | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const claims = this.oauthService.getIdentityClaims() as any;
    if (claims && claims.exp) {
      const exp = claims.exp;
      return exp;
    }
    return null;
  }

  private async tryLoginAsync<T>(state?: T): Promise<boolean | T> {
    await this.oauthService.loadDiscoveryDocumentAndTryLogin();
    await this.oauthService.tryLogin({});
    return state ? state : this.isLoggedIn;
  }

  tryLogin<T>(state?: T): Observable<boolean | T> {
    return from(this.tryLoginAsync(state));
  }

  initiateAuthenticationFlow() {
    if (this.configService.auth !== null) {
      this.oauthService
        .loadDiscoveryDocument(this.configService.auth.openIdDocument)
        .then(() => {
          this.oauthService.initImplicitFlow();
        });
    } else {
      this.loginService.getProviders(this.providersUrl).subscribe(value => {
        // [{"scopeDelimiter": " ", "responseType": "code", "url": "http://localhost/api/1/auth/ping", "authorizationEndpoint": "https://login.microsoftonline.com/906aefe9-76a7-4f65-b82d-5ec20775d5aa/oauth2/v2.0/authorize", "redirectUri": "http://localhost/api/1/auth/ping", "type": "2.0", "scope": "openid%20profile%20email", "name": "Comcast SSO", "requiredUrlParams": ["scope"], "clientId": "afd6f86d-105e-4a84-8093-3c073ecd6c73"}]
        if (!Array.isArray(value)) {
          console.error('Providers are not an array');
          return;
        }
        this.configService.setupConfig(value[0]);
        this.configureOAuthService();
        this.oauthService
          .loadDiscoveryDocument(this.configService.auth?.openIdDocument)
          .then(() => {
            this.oauthService.initImplicitFlow();
          });
      });
    }
    return Promise.resolve();
  }
  handleTokenError(route: string): Observable<string | boolean> {
    // Todo handle error
    return this.tryLogin(route);
  }

  silentRefresh() {
    this.oauthService.loadDiscoveryDocument();
    // if (!this.oauthService.hasValidAccessToken()) {
    return this.oauthService
      .silentRefresh()
      .then((info: unknown) => {
        localStorage.setItem('encodeAzureID', this.encodeAzureToken());
        console.log(' Token Refreshed successfully!', info);
      })
      .catch((err: unknown) => console.log('silentt refresh error', err));
    // }
  }

  private async logoutAsync(): Promise<boolean> {
    await this.oauthService.loadDiscoveryDocument(
      this.configService.auth?.openIdDocument,
    );
    this.oauthService.postLogoutRedirectUri =
      this.configService.getLogoutRedirectUri();
    this.oauthService.logOut();
    return !this.isLoggedIn;
  }

  logout(): Observable<boolean> {
    return from(this.logoutAsync());
  }

  public get isLoggedIn(): boolean {
    const hasIdToken = this.oauthService.hasValidIdToken();
    const hasAccessToken = this.oauthService.hasValidAccessToken();
    localStorage.setItem('encodeAzureID', this.encodeAzureToken());
    if (hasIdToken && hasAccessToken) {
      localStorage.setItem('logintoken', 'testToken');
      // this.getJWTToken().subscribe(value => localStorage.setItem('logintoken', value.token));
      return true;
    } else {
      return false;
    }
  }

  /**
   * Encode Token
   */
  encodeAzureToken(): string {
    const date: Date = new Date();
    const jObj = {
      tokenType: 'AZURE_AD',
      token: localStorage['id_token'],
      timestamp: date.getMilliseconds(),
      nonce: this.generateRandomString(16),
    };
    const obj = JSON.stringify(jObj);
    const encodeString = window.btoa(obj);
    return encodeString;
  }

  generateRandomString(length: number): string {
    let text = '';
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    for (let i = 0; i < length; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  authenticate() {
    localStorage.clear();
    this.initiateAuthenticationFlow();
  }
}
